import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { LedgerAccountAutocomplete } from './LedgerAccountAutocomplete';
import { excelCellInputClass } from './accountingUi';
import { formatMoney } from '../../utils/accountingHelpers';

const thClass =
  'border border-gray-300 bg-[#e8eef4] px-2 py-2 text-center text-xs font-semibold uppercase tracking-wide text-gray-700';
const tdClass = 'border border-gray-300 p-0 align-middle text-sm';
const serialClass =
  'border border-gray-300 bg-[#f8fafc] px-2 py-2 text-center text-xs font-medium text-gray-500';

export function VoucherLinesGrid({
  rows,
  config,
  total,
  onUpdateRow,
  onAddRow,
  onRemoveRow,
  onRowFocus,
}) {
  const showChequeFields = config?.detailChequeFields;

  return (
    <div className="overflow-x-auto pb-1">
      <table className="voucher-excel-grid w-full min-w-[920px] border-collapse bg-white text-sm">
        <thead>
          <tr>
            <th className={`${thClass} w-10`}>#</th>
            <th className={`${thClass} min-w-[7rem]`}>Account Code</th>
            <th className={`${thClass} min-w-[14rem]`}>Account Name</th>
            <th className={`${thClass} w-28`}>Amount</th>
            {showChequeFields ? (
              <>
                <th className={`${thClass} w-28`}>Cheque No</th>
                <th className={`${thClass} w-32`}>Maturity</th>
              </>
            ) : null}
            <th className={`${thClass} min-w-[12rem]`}>Narration</th>
            <th className={`${thClass} w-10`} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.Id || 'new'}-${index}`} className="group hover:bg-sky-50/40">
              <td className={serialClass}>{index + 1}</td>
              <td className={tdClass}>
                <LedgerAccountAutocomplete
                  variant="cell"
                  displayField="code"
                  source="general"
                  placeholder="Code"
                  value={row.Account}
                  onSelect={(account) => onUpdateRow(index, { Account: account, AccountId: account.Id })}
                  onFocus={() => onRowFocus?.(index)}
                />
              </td>
              <td className={tdClass}>
                <LedgerAccountAutocomplete
                  variant="cell"
                  displayField="name"
                  source="general"
                  placeholder="Account name"
                  value={row.Account}
                  onSelect={(account) => onUpdateRow(index, { Account: account, AccountId: account.Id })}
                  onFocus={() => onRowFocus?.(index)}
                />
              </td>
              <td className={tdClass}>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className={`${excelCellInputClass} text-right font-medium tabular-nums`}
                  value={row.Amount || ''}
                  onChange={(e) => onUpdateRow(index, { Amount: Number(e.target.value || 0) })}
                  onFocus={() => onRowFocus?.(index)}
                />
              </td>
              {showChequeFields ? (
                <>
                  <td className={tdClass}>
                    <input
                      className={excelCellInputClass}
                      value={row.ChequeNo || ''}
                      onChange={(e) => onUpdateRow(index, { ChequeNo: e.target.value })}
                    />
                  </td>
                  <td className={tdClass}>
                    <input
                      type="date"
                      className={excelCellInputClass}
                      value={row.MaturityDate || ''}
                      onChange={(e) => onUpdateRow(index, { MaturityDate: e.target.value })}
                    />
                  </td>
                </>
              ) : null}
              <td className={tdClass}>
                <input
                  className={excelCellInputClass}
                  placeholder="Narration"
                  value={row.Description || ''}
                  onChange={(e) => onUpdateRow(index, { Description: e.target.value })}
                  onFocus={() => onRowFocus?.(index)}
                />
              </td>
              <td className={`${serialClass} p-0`}>
                <button
                  type="button"
                  className="flex h-full w-full items-center justify-center p-2 text-red-500 opacity-60 hover:bg-red-50 hover:opacity-100"
                  onClick={() => onRemoveRow(index)}
                  title="Remove line"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="voucher-excel-grid-foot">
          <tr className="bg-[#e8eef4] font-semibold text-gray-800">
            <td colSpan={3} className="border border-gray-300 px-3 py-2.5 text-right text-xs uppercase tracking-wide">
              Total
            </td>
            <td className="border border-gray-300 px-2 py-2.5 text-right tabular-nums">{formatMoney(total)}</td>
            <td
              colSpan={showChequeFields ? 4 : 2}
              className="border border-gray-300 px-3 py-2.5 text-right"
            >
              <button
                type="button"
                onClick={onAddRow}
                className="inline-flex items-center gap-1.5 rounded-md border border-primary/30 bg-white px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/5"
              >
                <Plus className="h-3.5 w-3.5" />
                Add row
              </button>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
